import React from 'react'
import { Box, Stack, TextField, Typography, useTheme } from '@mui/material'
import { ConnectedDotsIcon } from '../../components/Icons/ConnectedDotsIcon'
import { TransitionButton } from '../../components/Buttons/TransitionButton'
import { AccountRecoveryRequest } from '../../components/AuthContext/types'
import { validatePassword } from '../../services/formhelper.service'
import {
  sendTrackingEvent,
  toButtonEvent
} from '../../services/amplitude.service'
import { TroubleButtonEvent } from './types'

type State = {
  email: string
  validationErrors: Record<string, string>
}

const ForgotPasswordComponent: React.FC<{
  onSubmit: (data: AccountRecoveryRequest) => void
  loading: boolean
}> = ({ onSubmit, loading }): JSX.Element => {
  const theme = useTheme()
  const [values, setValues] = React.useState<State>({
    email: '',
    validationErrors: {}
  })

  const validate =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      let validationErrors = {}
      if (prop === 'email' && validatePassword(event.target.value)) {
        validationErrors = { password: 'error' }
      }
      return validationErrors
    }

  const handleChange =
    (prop: keyof State) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setValues({
        ...values,
        [prop]: event.target.value,
        validationErrors: validate(prop)(event)
      })
    }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    onSubmit({
      email: values.email
    })
  }

  const isDisabled = () => loading || !`${values.email}`.trim()

  return (
    <div style={{ marginTop: theme.spacing(2) }}>
      <Typography
        component="h1"
        variant="h5"
        align="center"
        color="text.primary"
        gutterBottom
      >
        Password Recovery
      </Typography>

      <div style={{ textAlign: 'center' }}>
        <ConnectedDotsIcon sx={{ height: '1em', width: '2em' }} />
      </div>

      <Stack
        component="form"
        spacing={2}
        noValidate
        onSubmit={handleSubmit}
        sx={{
          p: theme.breakpoints.only('xs') ? 0 : 2
        }}
      >
        <TextField
          required
          id="email"
          label="Email"
          type="email"
          value={values.email}
          onChange={handleChange('email')}
          sx={{ minWidth: 300 }}
        />

        <Typography
          component="div"
          variant="body1"
          align="right"
          sx={{ mt: 2 }}
        >
          <a
            href="https://paceline.zendesk.com/"
            style={{ textDecoration: 'none' }}
            onClick={() => sendTrackingEvent(toButtonEvent(TroubleButtonEvent))}
          >
            Having trouble getting in?
          </a>{' '}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center'
          }}
        >
          <TransitionButton
            text="Send"
            type="submit"
            loading={loading}
            style={{ width: '8rem' }}
            disabled={isDisabled()}
          />
        </Box>
      </Stack>
    </div>
  )
}

export default ForgotPasswordComponent
